/**
 * Keeps a CI queue's hydration across the chart round-trip (BAR-2786).
 *
 * Opening a patient from a queue unmounts the list, and coming back used to
 * re-hydrate the whole cohort — ~1,000 of the rep's 50k-point Medplum minute
 * after BAR-2780, on every return, for reps who return every 30–60 seconds.
 * Module scope, so it outlives the page but not a reload.
 *
 * Two rules keep it honest:
 *   - An entry expires `QUEUE_CACHE_TTL_MS` after it was first LOADED, not after
 *     it was last used, so a rep bouncing in and out cannot keep other reps'
 *     changes out of view indefinitely.
 *   - A patient opened from any queue is re-hydrated on the next return, since
 *     that is the one row the rep has just changed.
 */

export const QUEUE_CACHE_TTL_MS = 5 * 60 * 1000;

export interface QueueCacheEntry<S> {
  state: S;
  patients: Set<string>;
  notes: Set<string>;
  poolFetched: boolean;
  /** When this scope was first hydrated; the TTL runs from here. */
  loadedAt: number;
  /** When this entry was last put on screen; patients opened after it are stale. */
  shownAt: number;
}

const entries = new Map<string, QueueCacheEntry<unknown>>();
const openedAt = new Map<string, number>();

/** A fresh entry for `key`, or undefined when there is none or it has expired. */
export function readQueueCache<S>(key: string, now = Date.now()): QueueCacheEntry<S> | undefined {
  const entry = entries.get(key) as QueueCacheEntry<S> | undefined;

  if (entry && now - entry.loadedAt >= QUEUE_CACHE_TTL_MS) {
    entries.delete(key);
    return undefined;
  }

  return entry;
}

/** Stores what is held for `key`, keeping the original `loadedAt`. */
export function writeQueueCache<S>(
  key: string,
  entry: Omit<QueueCacheEntry<S>, 'loadedAt' | 'shownAt'>,
  now = Date.now()
): void {
  const existing = readQueueCache<S>(key, now);

  entries.set(key, {
    ...entry,
    patients: new Set(entry.patients),
    notes: new Set(entry.notes),
    loadedAt: existing?.loadedAt ?? now,
    shownAt: existing?.shownAt ?? now,
  });
}

/**
 * Marks `key` as on screen and returns the patients opened since it last was —
 * the ones whose hydration is stale.
 */
export function takeReopenedPatients(key: string, now = Date.now()): string[] {
  const entry = entries.get(key);

  if (!entry) {
    return [];
  }

  const since = entry.shownAt;
  entry.shownAt = now;

  return [...openedAt].filter(([, at]) => at >= since).map(([reference]) => reference);
}

/** Called when a rep opens a patient from a queue row. */
export function rememberOpenedPatient(reference: string, now = Date.now()): void {
  openedAt.set(reference, now);
}

/** For tests. */
export function clearQueueCache(): void {
  entries.clear();
  openedAt.clear();
}
